// frontend/src/pages/EditServicePage.tsx
// Mobile-first page to edit provider services

import React, { useState, useEffect } from 'react';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../utils/toast';
import {
  useProviderService,
  useUpdateProviderService,
  useDeleteProviderService
} from '@/hooks/useProviderServicesQueries';
import { ProviderServiceUpdateData } from '@/lib/api/providerServicesService';
import LoadingSpinner from '@/components/LoadingSpinner';

const EditServicePage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const serviceId = id ? parseInt(id, 10) : 0;

  // Form state
  const [formData, setFormData] = useState<ProviderServiceUpdateData>({
    service_name: '',
    description: '',
    price_range: '',
    estimated_duration: '',
    is_active: true,
    is_featured: false,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Data and mutations
  const { data: service, isLoading: isLoadingService, error } = useProviderService(serviceId);
  const updateServiceMutation = useUpdateProviderService();
  const deleteServiceMutation = useDeleteProviderService();

  // Initialize form data when service loads
  useEffect(() => {
    if (service) {
      const initialData = {
        service_name: service.service_name || '',
        description: service.description || '',
        price_range: service.price_range || '',
        estimated_duration: service.estimated_duration || '',
        is_active: service.is_active,
        is_featured: service.is_featured,
      };
      setFormData(initialData);
      setHasChanges(false);
    }
  }, [service]);

  // Handle form input changes
  const handleInputChange = (field: keyof ProviderServiceUpdateData, value: any) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      setHasChanges(true);
      return newData;
    });

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.service_name?.trim()) {
      newErrors.service_name = 'Service name is required';
    } else if (formData.service_name.length > 255) {
      newErrors.service_name = 'Service name must be less than 255 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description must be less than 1000 characters';
    }

    if (formData.price_range && formData.price_range.length > 100) {
      newErrors.price_range = 'Price range must be less than 100 characters';
    }

    if (formData.estimated_duration && formData.estimated_duration.length > 50) {
      newErrors.estimated_duration = 'Duration must be less than 50 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    try {
      await updateServiceMutation.mutateAsync({
        id: serviceId,
        data: {
          ...formData,
          service_name: formData.service_name?.trim(),
          description: formData.description?.trim() || undefined,
          price_range: formData.price_range?.trim() || undefined,
          estimated_duration: formData.estimated_duration?.trim() || undefined,
        }
      });

      setHasChanges(false);
      toast.success('Service updated successfully!');
      navigate('/provider-services');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update service');
    }
  };

  // Handle service deletion
  const handleDelete = async () => {
    const confirmMessage = `Are you sure you want to delete "${service?.service_name}"? This action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteServiceMutation.mutateAsync(serviceId);
        toast.success('Service deleted successfully!');
        navigate('/provider-services');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete service');
      }
    }
  };

  // Handle navigation with unsaved changes
  const handleBack = () => {
    if (hasChanges) {
      const confirmMessage = 'You have unsaved changes. Are you sure you want to leave?';
      if (window.confirm(confirmMessage)) {
        navigate('/provider-services');
      }
    } else {
      navigate('/provider-services');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 mb-2">Error loading service</div>
          <div className="text-sm text-gray-600 mb-4">{error.message}</div>
          <button
            onClick={() => navigate('/provider-services')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  if (isLoadingService) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
          <LoadingSpinner message="Loading service..." />
        </div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-600 mb-4">Service not found</div>
          <button
            onClick={() => navigate('/provider-services')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
          >
            Back to Services
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-4xl px-4 py-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={handleBack}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-gray-900 sm:text-2xl truncate">
                Edit Service
              </h1>
              <p className="text-sm text-gray-600 sm:text-base">
                Update your service details
              </p>
            </div>
          </div>

          {/* Save/Delete Actions */}
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <div className="flex gap-2">
              <button
                onClick={handleSave}
                disabled={updateServiceMutation.isPending || !hasChanges}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {updateServiceMutation.isPending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Save Changes
                  </>
                )}
              </button>
            </div>

            <button
              onClick={handleDelete}
              disabled={deleteServiceMutation.isPending}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm font-medium"
            >
              {deleteServiceMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={16} />
                  Delete Service
                </>
              )}
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 sm:p-6">
            <div className="space-y-6">
              {/* Service Info */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Service Information
                  </h3>
                </div>

                {/* Service Type Info (Read-only) */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Service Type:</span>
                      <span className="text-sm text-gray-900">{service.service_type_name}</span>
                    </div>
                    {service.category_name && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">Category:</span>
                        <span className="text-sm text-gray-900">{service.category_name}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Service Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={formData.service_name || ''}
                    onChange={(e) => handleInputChange('service_name', e.target.value)}
                    placeholder="Enter service name"
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.service_name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.service_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.service_name}</p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={formData.description || ''}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe your service (optional)"
                    rows={4}
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-vertical ${
                      errors.description ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    {formData.description?.length || 0}/1000 characters
                  </p>
                </div>

                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price Range
                  </label>
                  <input
                    type="text"
                    value={formData.price_range || ''}
                    onChange={(e) => handleInputChange('price_range', e.target.value)}
                    placeholder="e.g., $50-100 or Contact for quote"
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.price_range ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.price_range && (
                    <p className="mt-1 text-sm text-red-600">{errors.price_range}</p>
                  )}
                </div>

                {/* Estimated Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Duration
                  </label>
                  <input
                    type="text"
                    value={formData.estimated_duration || ''}
                    onChange={(e) => handleInputChange('estimated_duration', e.target.value)}
                    placeholder="e.g., 2 hours or 1-2 days"
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.estimated_duration ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.estimated_duration && (
                    <p className="mt-1 text-sm text-red-600">{errors.estimated_duration}</p>
                  )}
                </div>
              </div>

              {/* Service Settings */}
              <div className="space-y-4 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  Service Settings
                </h3>

                {/* Status Toggles */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Service Status
                      </label>
                      <p className="text-sm text-gray-500">
                        Enable or disable this service for customers
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active || false}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {formData.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="text-sm font-medium text-gray-700">
                        Featured Service
                      </label>
                      <p className="text-sm text-gray-500">
                        Highlight this service in search results
                      </p>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_featured || false}
                        onChange={(e) => handleInputChange('is_featured', e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        {formData.is_featured ? 'Featured' : 'Not Featured'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Timestamps */}
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Service History
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="text-sm font-medium text-gray-700">Created</div>
                    <div className="text-sm text-gray-900">
                      {new Date(service.created_at).toLocaleString()}
                    </div>
                  </div>
                  {service.updated_at && service.updated_at !== service.created_at && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm font-medium text-gray-700">Last Updated</div>
                      <div className="text-sm text-gray-900">
                        {new Date(service.updated_at).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditServicePage;